import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar as MuiAvatar,
  Button,
  TextField,
  IconButton,
  Grid,
  Chip,
  Divider,
  Autocomplete,
  Paper,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  PhotoCamera,
  Info,
  FamilyRestroom,
  Language,
  Work,
  Save,
  Cancel,
  Edit,
  CalendarToday,
  Person,
  Public,
  School,
  Translate,
  Build,
  ChildFriendly,
  Face,
  WashRounded,
  Book,
  
} from '@mui/icons-material';
import axios from 'axios';
import baseUrl from '../config';
import countries from '../utlis';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';


// Predefined Avatars (Using more visually appealing images)
const preDefinedAvatars = [
  {
    id: 'avatar1',
    name: 'Happy Kid',
    imageUrl: '/avatars/happy_kid.jpg',
  },
  {
    id: 'avatar2',
    name: 'Adventurous Girl',
    imageUrl: '/avatars/adventurous_girl.jpg',
  },
  {
    id: 'avatar3',
    name: 'Smart Boy',
    imageUrl: '/avatars/smart_boy.jpg',
  },
  {
    id: 'avatar4',
    name: 'Creative Girl',
    imageUrl: '/avatars/creative_girl.jpg',
  },
  {
    id: 'avatar5',
    name: 'Energetic Boy',
    imageUrl: '/avatars/energetic_boy.jpg',
  },
  {
    id: 'avatar6',
    name: 'Joyful Girl',
    imageUrl: '/avatars/joyful_girl.jpg',
  },
  {
    id: 'avatar7',
    name: 'Curious Boy',
    imageUrl: '/avatars/curious_boy.jpg',
  },
  {
      id: 'avatar8',
      name: 'Friendly Girl',
      imageUrl: '/avatars/friendly_girl.jpg'
  }
];

// Animation Variants
const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeInOut" } },
    exit: { opacity: 0, y: -50, transition: { duration: 0.3 } }
};

const StudentProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAvatarSelectionMode, setIsAvatarSelectionMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    avatar: '',
    bio: '',
    skills: [],
    languages: [],
    dob: '',
    gender: '',
    parentName: '',
    nationality: null,
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [tempAvatarUrl, setTempAvatarUrl] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${baseUrl}api/students/profile`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setProfile(data);
        setFormData({
          name: data.name,
          avatar: data.avatar,
          bio: data.bio,
          skills: data.skills || [],
          languages: data.languages || [],
          dob: data.dob || '',
          gender: data.gender || '',
          nationality: data.nationality ? countries.find((c) => c.name === data.nationality) : null,
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };
    fetchProfile();
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setTempAvatarUrl(URL.createObjectURL(file));
      setFormData({ ...formData, avatar: '' }); // Clear any previously selected avatar ID
      setIsAvatarSelectionMode(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAvatarSelect = (avatarId) => {
    setFormData({ ...formData, avatar: avatarId });
    setIsAvatarSelectionMode(false);
    setTempAvatarUrl(null);
    setAvatarFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedFormData = new FormData();
    updatedFormData.append('name', formData.name);
    updatedFormData.append('bio', formData.bio);
    updatedFormData.append('languages', formData.languages.join(', '));
    updatedFormData.append('skills', formData.skills.join(', '));
    updatedFormData.append('dob', formData.dob);
    updatedFormData.append('gender', formData.gender);
    updatedFormData.append('parentName', formData.parentName);
    updatedFormData.append('nationality', formData.nationality?.name || '');
    if (avatarFile) {
      updatedFormData.append('avatar', avatarFile);
    } else if (formData.avatar) {
      updatedFormData.append('avatar', formData.avatar); // Send avatar ID or image URL
    } else if (profile && profile.avatar && !tempAvatarUrl) {
      updatedFormData.append('avatar', profile.avatar);
    }

    try {
      const { data } = await axios.put(`${baseUrl}api/students/profile`, updatedFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setProfile(data);
      setAvatarFile(null);
      setTempAvatarUrl(null);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setIsAvatarSelectionMode(false);
    setAvatarFile(null);
    setTempAvatarUrl(null);
    setFormData({
      name: profile.name,
      avatar: profile.avatar,
      bio: profile.bio,
      skills: profile.skills || [],
      languages: profile.languages || [],
      dob: profile.dob || '',
      gender: profile.gender || '',
      nationality: profile.nationality ? countries.find((c) => c.name === profile.nationality) : null,
    });
  };

  const formattedDob = formData.dob ? format(new Date(formData.dob), 'MMMM d,pppp') : 'Not specified';

    const renderAvatar = () => {
        if (tempAvatarUrl) {
            return (
                <MuiAvatar
                    src={tempAvatarUrl}
                    alt={formData.name}
                    sx={{ width: 150, height: 150, border: '5px solid #ffdb58', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}
                />
            );
        }
        if (formData.avatar) {
            const selectedAvatar = preDefinedAvatars.find((avatar) => avatar.id === formData.avatar);
            if (selectedAvatar) {
                return (
                    <MuiAvatar
                        src={selectedAvatar.imageUrl}
                        alt={selectedAvatar.name}
                        sx={{
                            width: 150,
                            height: 150,
                            border: '5px solid #ffdb58',
                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                        }}
                    />
                );
            }
        }
        if (profile?.avatar && typeof profile.avatar === 'string' && !isAvatarSelectionMode) {
            return (
                <MuiAvatar
                    src={`${baseUrl}${profile.avatar}`}
                    alt={formData.name}
                    sx={{ width: 150, height: 150, border: '5px solid #ffdb58', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}
                >
                    {formData.name[0]}
                </MuiAvatar>
            );
        }
        return (
            <MuiAvatar sx={{ width: 150, height: 150, border: '5px solid #ffdb58', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}>
                {formData.name[0]}
            </MuiAvatar>
        );
    };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: { xs: 2, md: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      >
        <Paper
          elevation={5}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            backgroundColor: 'linear-gradient(135deg, #f7f3e3, #ffffff)',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: '#4a148c',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ChildFriendly sx={{ mr: 1, color: '#f57c00' }} />
              Student Profile
            </Typography>
            {!isEditMode && (
              <Button
                variant="contained"
                onClick={() => setIsEditMode(true)}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#ffb300',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#f57c00' },
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Edit sx={{ mr: 1 }} />
                Edit Profile
              </Button>
            )}
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <AnimatePresence>
                <motion.div
                    key="profile-card"
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <Card
                    elevation={3}
                    sx={{
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                    >
                    <CardContent>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            {renderAvatar()}
                            {isEditMode && (
                            <Box sx={{ mt: 1 }}>
                                <Button
                                component="label"
                                variant="outlined"
                                size="small"
                                sx={{
                                    mr: 1,
                                    textTransform: 'none',
                                    backgroundColor: '#fff',
                                    color: '#4a148c',
                                    border: '1px solid #4a148c',
                                    '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    },
                                }}
                                >
                                <PhotoCamera sx={{ mr: 0.5, height: 16, width: 16 }} />
                                Upload
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleAvatarChange}
                                />
                                </Button>
                                <Button
                                variant="contained"
                                size="small"
                                onClick={() => setIsAvatarSelectionMode(true)}
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: '#ffb300',
                                    color: '#ffffff',
                                    '&:hover': { backgroundColor: '#f57c00' },
                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                                }}
                                >
                                <Face sx={{ mr: 0.5, height: 16, width: 16 }} />
                                Avatars
                                </Button>
                            </Box>
                            )}
                        </Box>

                        <AnimatePresence>
                            {isAvatarSelectionMode && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                            >
                                <Paper
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                }}
                                >
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{ color: '#4a148c', fontWeight: 600 }}
                                >
                                    Choose an Avatar
                                </Typography>
                                <Grid container spacing={2}>
                                    {preDefinedAvatars.map((avatar) => (
                                    <Grid item xs={6} sm={4} key={avatar.id}>
                                        <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        >
                                            <Card
                                            onClick={() => handleAvatarSelect(avatar.id)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s',
                                                '&:hover': { transform: 'scale(1.05)' },
                                                borderRadius: 2,
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                            }}
                                            >
                                            <CardContent
                                                sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                padding: 2,
                                                backgroundColor: 'transparent',
                                                }}
                                            >
                                                <MuiAvatar
                                                src={avatar.imageUrl}
                                                alt={avatar.name}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    mb: 1,
                                                    border: '3px solid #ffdb58',
                                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                                                }}
                                                />
                                                <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#555',
                                                    textAlign: 'center',
                                                    fontSize: '0.7rem',
                                                }}
                                                >
                                                {avatar.name}
                                                </Typography>
                                            </CardContent>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                    ))}
                                </Grid>
                                <Button
                                fullWidth
                                size="small"
                                onClick={() => setIsAvatarSelectionMode(false)}
                                sx={{
                                    mt: 2,
                                    textTransform: 'none',
                                    backgroundColor: '#e0e0e0',
                                    color: '#555',
                                    '&:hover': { backgroundColor: '#d0d0d0' },
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                }}
                                >
                                Cancel
                                </Button>
                                </Paper>
                            </motion.div>
                            )}
                        </AnimatePresence>

                        {isEditMode && !isAvatarSelectionMode && (
                            <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            sx={{ mt: 2 }}
                            InputProps={{
                                startAdornment: (
                                <InputAdornment position="start">
                                    <Person sx={{ color: '#7e57c2' }} />
                                </InputAdornment>
                                ),
                            }}
                            />
                        )}
                        {!isEditMode && (
                            <Typography
                            variant="h5"
                            sx={{ mt: 2, fontWeight: 700, color: '#4a148c' }}
                            >
                            {formData.name}
                            </Typography>
                        )}
                        </Box>

                        <Divider sx={{ my: 2, backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />

                        <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{
                            color: '#4a148c',
                            display: 'flex',
                            alignItems: 'center',
                            }}
                        >
                            <Person sx={{ mr: 1, color: '#7e57c2' }} />
                            Personal Details
                        </Typography>

                        <Box mb={2}>
                            <Typography variant="caption" sx={{ color: '#777' }}>
                            Date of Birth
                            </Typography>
                            {isEditMode ? (
                            <TextField
                                fullWidth
                                type="date"
                                name="dob"
                                value={formData.dob}
                                onChange={handleFormChange}
                                InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                    <CalendarToday sx={{ color: '#7e57c2' }} />
                                    </InputAdornment>
                                ),
                                }}
                            />
                            ) : (
                            <Typography sx={{ color: '#555' }}>{formattedDob}</Typography>
                            )}
                        </Box>

                        <Box mb={2}>
                            <Typography variant="caption" sx={{ color: '#777' }}>
                            Gender
                            </Typography>
                            {isEditMode ? (
                            <FormControl fullWidth>
                                <InputLabel id="gender-label" sx={{ color: '#777' }}>
                                Gender
                                </InputLabel>
                                <Select
                                labelId="gender-label"
                                name="gender"
                                value={formData.gender}
                                onChange={handleFormChange}
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1c4e9',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                    boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                    },
                                }}
                                >
                                <MenuItem value="Male" sx={{ color: '#555' }}>Male</MenuItem>
                                <MenuItem value="Female" sx={{ color: '#555' }}>Female</MenuItem>
                                </Select>
                            </FormControl>
                            ) : (
                            <Typography sx={{ color: '#555' }}>
                                {formData.gender || 'Not specified'}
                            </Typography>
                            )}
                        </Box>

                        <Box mb={2}>
                            <Typography variant="caption" sx={{ color: '#777' }}>
                            Nationality
                            </Typography>
                            {isEditMode ? (
                            <Autocomplete
                                options={countries}
                                getOptionLabel={(option) => option.name}
                                value={formData.nationality}
                                onChange={(event, newValue) =>
                                setFormData({ ...formData, nationality: newValue })
                                }
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Public sx={{ color: '#7e57c2' }} />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1c4e9',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7e57c2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7e57c2',
                                            boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                        },
                                    }}
                                />
                                )}
                            />
                            ) : (
                            <Typography sx={{ color: '#555' }}>
                                {formData.nationality?.name || 'Not specified'}
                            </Typography>
                            )}
                        </Box>

                        <Box mb={2}>
                            <Typography variant="caption" sx={{ color: '#777' }}>
                            Parent/Guardian
                            </Typography>
                            {isEditMode ? (
                            <TextField
                                fullWidth
                                name="parentName"
                                value={formData.parentName}
                                onChange={handleFormChange}
                                InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                    <FamilyRestroom sx={{ color: '#7e57c2' }} />
                                    </InputAdornment>
                                ),
                                }}
                                sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1c4e9',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                    boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                },
                                }}
                            />
                            ) : (
                            <Typography sx={{ color: '#555' }}>
                                {formData.parentName || 'Not specified'}
                            </Typography>
                            )}
                        </Box>
                        </Box>

                        <Divider sx={{ my: 2, backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />

                        <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{
                            color: '#4a148c',
                            display: 'flex',
                            alignItems: 'center',
                            }}
                        >
                            <WashRounded sx={{ mr: 1, color: '#7e57c2' }} />
                            Skills
                        </Typography>
                        {isEditMode ? (
                            <TextField
                            fullWidth
                            placeholder="Comma separated skills"
                            value={formData.skills.join(', ')}
                            onChange={(e) =>
                                setFormData({
                                ...formData,
                                skills: e.target.value.split(',').map((s) => s.trim()),
                                })
                            }
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d1c4e9',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7e57c2',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7e57c2',
                                boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                },
                            }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {formData.skills.length > 0 ? (
                                formData.skills.map((skill) => (
                                <Chip
                                    key={skill}
                                    label={skill}
                                    size="small"
                                    sx={{
                                    backgroundColor: '#ede7f6',
                                    color: '#5e35b1',
                                    margin: '2px',
                                    }}
                                />
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ color: '#777' }}>
                                No skills added
                                </Typography>
                            )}
                            </Box>
                        )}
                        </Box>

                        <Divider sx={{ my: 2, backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />

                        <Box>
                        <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{
                            color: '#4a148c',
                            display: 'flex',
                            alignItems: 'center',
                            }}
                        >
                            <Language sx={{ mr: 1, color: '#7e57c2' }} />
                            Languages
                        </Typography>
                        {isEditMode ? (
                            <TextField
                            fullWidth
                            placeholder="Comma separated languages"
                            value={formData.languages.join(', ')}
                            onChange={(e) =>
                                setFormData({
                                ...formData,
                                languages: e.target.value.split(',').map((l) => l.trim()),
                                })
                            }
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d1c4e9',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7e57c2',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7e57c2',
                                boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                },
                            }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {formData.languages.length > 0 ? (
                                formData.languages.map((lang) => (
                                <Chip
                                    key={lang}
                                    label={lang}
                                    size="small"
                                    sx={{
                                    backgroundColor: '#ede7f6',
                                    color: '#5e35b1',
                                    margin: '2px',
                                    }}
                                />
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ color: '#777' }}>
                                No languages added
                                </Typography>
                            )}
                            </Box>
                        )}
                        </Box>

                        {isEditMode && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                            variant="outlined"
                            onClick={handleCancelEdit}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: '#e0e0e0',
                                color: '#555',
                                '&:hover': { backgroundColor: '#d0d0d0' },
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            }}
                            >
                            <Cancel sx={{ mr: 1 }} />
                            Cancel
                            </Button>
                            <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: '#ffb300',
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#f57c00' },
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                            }}
                            >
                            <Save sx={{ mr: 1 }} />
                            Save
                            </Button>
                        </Box>
                        )}
                    </CardContent>
                    </Card>
                </motion.div>
              </AnimatePresence>
            </Grid>

            <Grid item xs={12} md={8}>
                <AnimatePresence>
                    <motion.div
                        key="about-academic-card"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{height: '100%'}}
                    >
                        <Card
                        elevation={3}
                        sx={{
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        >
                        <CardContent style={{flex: 1}}>
                            <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{
                                color: '#4a148c',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            >
                            <Info sx={{ mr: 1, color: '#7e57c2' }} />
                            About Me
                            </Typography>
                            {isEditMode ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                name="bio"
                                value={formData.bio}
                                onChange={handleFormChange}
                                placeholder="Tell us about yourself..."
                                sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1c4e9',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7e57c2',
                                    boxShadow: '0 0 0 2px rgba(126, 87, 194, 0.2)',
                                },
                                }
                              }
                                 />
                             
                          
                            ) : (
                            <Typography variant="body1" paragraph sx={{ color: '#555', whiteSpace: 'pre-line' }}>
                                {formData.bio || 'No bio available.'}
                            </Typography>
                            )}

                            <Divider sx={{ my: 3, backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />

                            <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{
                                color: '#4a148c',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            >
                            <Book sx={{ mr: 1, color: '#7e57c2' }} />
                            Academic Information
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#777' }}>
                            Academic information would be displayed here
                            </Typography>
                        </CardContent>
                        </Card>
                    </motion.div>
                </AnimatePresence>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default StudentProfilePage;
